import { NavLink } from 'react-router-dom';
import type { EmployeeRole } from '@trackkm/shared';
import { useAuthStore } from '@/stores/auth';

interface BottomNavProps {
  readonly unreadCount?: number;
}

interface BottomNavItem {
  readonly label: string;
  readonly path: string;
  readonly icon: React.ReactNode;
  readonly roles: readonly EmployeeRole[];
  readonly badgeKey?: string;
}

const EMPLOYEE_NAV: readonly BottomNavItem[] = [
  {
    label: 'Home',
    path: '/',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    roles: ['employee', 'manager', 'admin', 'hr'],
  },
  {
    label: 'Trips',
    path: '/trips',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    roles: ['employee', 'manager', 'admin', 'hr'],
  },
  {
    label: 'New Trip',
    path: '/trips/new',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    roles: ['employee', 'manager'],
  },
  {
    label: 'Approvals',
    path: '/approvals',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    roles: ['manager', 'admin'],
  },
  {
    label: 'Reports',
    path: '/reports',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    roles: ['manager', 'admin', 'hr'],
  },
  {
    label: 'Analytics',
    path: '/analytics',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
      </svg>
    ),
    roles: ['manager', 'admin', 'hr'],
  },
  {
    label: 'More',
    path: '/notifications',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
      </svg>
    ),
    roles: ['employee', 'manager', 'admin', 'hr'],
    badgeKey: 'notifications',
  },
];

export function BottomNav({ unreadCount = 0 }: BottomNavProps) {
  const employee = useAuthStore((s) => s.employee);
  const role = employee?.role ?? 'employee';

  const filteredItems = EMPLOYEE_NAV.filter((item) =>
    item.roles.includes(role),
  ).slice(0, 5); // max 5 items

  const badgeCounts: Readonly<Record<string, number>> = {
    notifications: unreadCount,
  };

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 bottom-nav">
      <div className="flex items-center justify-around px-2 pt-2 pb-1">
        {filteredItems.map((item) => {
          const badge = item.badgeKey ? badgeCounts[item.badgeKey] ?? 0 : 0;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `
                flex flex-col items-center gap-0.5 px-2 py-1 min-w-[56px] rounded-lg transition-colors relative
                ${isActive
                  ? 'text-brand-blue-600'
                  : 'text-gray-400 hover:text-gray-600'
                }
              `}
            >
              <span className="relative">
                {item.icon}
                {badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
